import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { AlertTriangle, ShieldAlert, Phone, ExternalLink, Check } from "lucide-react";

// =================== Long disclaimer content (EN + ES) ===================
const CONTENT = {
  en: {
    headline: "Important Notice — Please Read Carefully",
    intro: "This platform provides general workplace guidance, stress-management support, and coaching-style insights intended to help users reflect on work-related challenges. It does not provide medical, psychological, psychiatric, legal, or therapeutic services. By using this platform, you acknowledge and agree to the following:",
    sections: [
      {
        title: "1. Not therapy, medical care, or professional advice",
        bullets: [
          "Not a substitute for professional mental health care, therapy, counseling, medical treatment, or crisis support.",
          "Does not diagnose conditions, assess clinical risk, or provide treatment recommendations.",
          "Any content is for informational and educational purposes only.",
          "You are solely responsible for how you interpret and act on the information provided.",
        ],
      },
      {
        title: "2. No liability for decisions or outcomes",
        bullets: [
          "Decisions you make based on this platform are your own.",
          "The company, its affiliates, employees, consultants, and technology providers are not responsible for actions taken, decisions made, or consequences resulting from your use of the platform.",
          "You agree not to rely on the platform as the sole basis for decisions affecting your health, safety, employment, relationships, or wellbeing.",
        ],
      },
      {
        title: "3. AI-assisted guidance & limitations",
        bullets: [
          "This platform uses artificial intelligence to generate suggestions and reflections.",
          "AI outputs may be incomplete, incorrect, or not appropriate for your situation.",
          "AI responses are probabilistic and may not fully understand emotional nuance or personal circumstances.",
          "Human judgment is always required before relying on any output.",
        ],
      },
      {
        title: "4. Crisis & safety disclaimer (critical)",
        bullets: [
          "This platform is NOT equipped to provide emergency or crisis support.",
          "If you are experiencing thoughts of harming yourself or others, suicidal thoughts, overwhelming distress, or a mental health emergency — do not rely on this platform. Seek immediate help.",
          "United States: Call or text 988 — Suicide & Crisis Lifeline (24/7). In any emergency call 911.",
          "Outside the U.S.: Contact your local emergency number or visit opencounseling.com/suicide-hotlines",
          "Using this platform does NOT create a therapist-client, doctor-patient, or counselor-client relationship.",
        ],
      },
      {
        title: "5. Data & safety monitoring",
        bullets: [
          "Interactions may be logged to improve system quality and ensure platform safety.",
          "The platform cannot guarantee detection of crisis situations.",
          "Do not submit highly sensitive personal, medical, or confidential information unless you are authorized and comfortable doing so.",
        ],
      },
      {
        title: "6. Acceptance",
        bullets: [
          "You understand the platform's limitations.",
          "You accept full responsibility for your decisions.",
          "You agree to the Terms of Service and Privacy Policy.",
          "You acknowledge that this platform is supportive guidance only, not clinical care.",
        ],
      },
    ],
    consent: "I have read and accepted the conditions above. I understand this is not clinical care and that I'm responsible for my decisions.",
    accept_btn: "Accept and continue",
    crisis_btn: "I'm in crisis — show me help",
  },
  es: {
    headline: "Aviso importante — léelo con atención",
    intro: "Esta plataforma ofrece orientación general sobre el trabajo, apoyo para manejar el estrés y reflexiones tipo coaching, pensadas para ayudarte a reflexionar sobre desafíos laborales. NO ofrece servicios médicos, psicológicos, psiquiátricos, legales ni terapéuticos. Al usar esta plataforma, reconoces y aceptas lo siguiente:",
    sections: [
      {
        title: "1. No es terapia, atención médica ni consejo profesional",
        bullets: [
          "No sustituye atención profesional de salud mental, terapia, consejería, tratamiento médico ni apoyo en crisis.",
          "No diagnostica condiciones, no evalúa riesgo clínico ni da recomendaciones de tratamiento.",
          "El contenido es solo informativo y educativo.",
          "Eres responsable de cómo interpretas y actúas sobre la información provista.",
        ],
      },
      {
        title: "2. Sin responsabilidad por decisiones o resultados",
        bullets: [
          "Las decisiones que tomes basadas en esta plataforma son tuyas.",
          "La empresa, sus afiliados, empleados, consultores y proveedores tecnológicos no son responsables de acciones, decisiones o consecuencias derivadas del uso de la plataforma.",
          "No debes usar esta plataforma como única base para decisiones sobre tu salud, seguridad, empleo, relaciones o bienestar.",
        ],
      },
      {
        title: "3. Guía asistida por IA y sus límites",
        bullets: [
          "Esta plataforma usa inteligencia artificial para generar sugerencias y reflexiones.",
          "Las respuestas de IA pueden ser incompletas, incorrectas o no apropiadas para tu situación.",
          "Las respuestas son probabilísticas y pueden no entender los matices emocionales ni tus circunstancias personales.",
          "Siempre se requiere juicio humano antes de actuar sobre cualquier salida de la IA.",
        ],
      },
      {
        title: "4. Crisis y seguridad (crítico)",
        bullets: [
          "Esta plataforma NO está equipada para emergencias ni apoyo en crisis.",
          "Si tienes pensamientos de hacerte daño o dañar a otros, ideación suicida, angustia desbordante o una emergencia de salud mental — no dependas de esta plataforma. Busca ayuda inmediata.",
          "EE.UU.: llama o envía mensaje al 988 — Suicide & Crisis Lifeline (24/7). En emergencia, 911.",
          "Fuera de EE.UU.: contacta tu número local de emergencia o visita opencounseling.com/suicide-hotlines",
          "Usar esta plataforma NO crea una relación terapeuta-paciente, médico-paciente, ni consejero-cliente.",
        ],
      },
      {
        title: "5. Datos y monitoreo de seguridad",
        bullets: [
          "Las interacciones pueden registrarse para mejorar la calidad y la seguridad de la plataforma.",
          "La plataforma no puede garantizar la detección de situaciones de crisis.",
          "No envíes información personal, médica o confidencial muy sensible salvo que estés autorizado y a gusto haciéndolo.",
        ],
      },
      {
        title: "6. Aceptación",
        bullets: [
          "Entiendes las limitaciones de la plataforma.",
          "Aceptas plena responsabilidad por tus decisiones.",
          "Aceptas los Términos del Servicio y la Política de Privacidad.",
          "Reconoces que esta plataforma es orientación de apoyo, no atención clínica.",
        ],
      },
    ],
    consent: "He leído y acepto las condiciones anteriores. Entiendo que esto no es atención clínica y que soy responsable de mis decisiones.",
    accept_btn: "Acepto y continuar",
    crisis_btn: "Estoy en crisis — muéstrame ayuda",
  },
};

const SHORT_VERSION = {
  en: "This platform provides non-clinical workplace and stress-management guidance and is not therapy or crisis support. AI outputs may be inaccurate. You are responsible for decisions you make. If you are in crisis or having suicidal thoughts, seek immediate professional help (U.S. 988 / local emergency services).",
  es: "Esta plataforma ofrece orientación no clínica sobre el trabajo y manejo del estrés, NO es terapia ni apoyo en crisis. Las respuestas de IA pueden ser inexactas. Eres responsable de tus decisiones. Si estás en crisis o tienes pensamientos suicidas, busca ayuda profesional inmediata (EE.UU. 988 / servicios locales de emergencia).",
};

// =================== Long Modal (first-use, blocking) ===================
export const DisclaimerModal = ({ open, onAccept, onClose }) => {
  const { language } = useApp();
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const c = CONTENT[language] || CONTENT.en;

  useEffect(() => {
    if (open) setConsented(false);
  }, [open]);

  if (!open) return null;

  const handleAccept = async () => {
    if (!consented || submitting) return;
    setSubmitting(true);
    try {
      await onAccept();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="disclaimer-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-2xl w-full max-h-[90vh] bg-[#121214] border border-[#FFB800]/30 rounded-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-start gap-4 bg-gradient-to-br from-[#FFB800]/8 to-transparent">
          <div className="w-11 h-11 rounded-xl bg-[#FFB800]/15 border border-[#FFB800]/40 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-[#FFB800]" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB800] mb-1">
              / {language === "es" ? "Antes de empezar" : "Before you start"}
            </div>
            <h2 className="font-display font-bold text-xl lg:text-2xl">{c.headline}</h2>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-sm leading-relaxed">
          <p className="text-white/80">{c.intro}</p>
          {c.sections.map((sec, i) => (
            <div key={i}>
              <h3 className="font-display font-semibold text-white mb-2 text-base">{sec.title}</h3>
              <ul className="space-y-1.5 pl-1">
                {sec.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2 text-white/70">
                    <span className="text-[#FFB800] mt-1.5 flex-shrink-0">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-4 flex items-start gap-3">
            <Phone className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
            <div className="text-white/85 text-sm">
              {language === "es"
                ? "Si estás en crisis o tienes pensamientos de hacerte daño, contacta YA al 988 (EE.UU.) o tu línea local de emergencia."
                : "If you are in crisis or having thoughts of self-harm, contact 988 (U.S.) or your local emergency line RIGHT NOW."}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10 space-y-4 bg-[#0A0A0A]/60">
          <label className="flex items-start gap-3 cursor-pointer text-sm text-white/85" data-testid="disclaimer-consent-label">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#006CFF]"
              data-testid="disclaimer-consent-checkbox"
            />
            <span>{c.consent}</span>
          </label>
          <div className="flex flex-col-reverse sm:flex-row gap-2 items-stretch sm:items-center sm:justify-between">
            <a
              href="https://www.opencounseling.com/suicide-hotlines"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FF3B30] hover:text-[#FF5A4F] text-xs font-mono uppercase tracking-[0.15em] flex items-center gap-1.5"
              data-testid="disclaimer-crisis-link"
            >
              {c.crisis_btn} <ExternalLink className="w-3 h-3" />
            </a>
            <Button
              onClick={handleAccept}
              disabled={!consented || submitting}
              className="bg-[#006CFF] hover:bg-[#0052CC] text-white px-6 disabled:opacity-40"
              data-testid="disclaimer-accept-btn"
            >
              {submitting ? "..." : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {c.accept_btn}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================== Short Crisis Banner (re-surface after crisis keywords) ===================
export const CrisisDisclaimerBanner = ({ visible, onDismiss }) => {
  const { language } = useApp();
  if (!visible) return null;
  return (
    <div
      className="rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/40 p-4 my-3 flex items-start gap-3"
      data-testid="crisis-disclaimer-banner"
      role="alert"
    >
      <AlertTriangle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-display font-semibold text-white mb-1">
          {language === "es" ? "Recordatorio importante" : "Important reminder"}
        </div>
        <p className="text-white/80 leading-relaxed">{SHORT_VERSION[language] || SHORT_VERSION.en}</p>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-white/40 hover:text-white text-xs font-mono uppercase tracking-[0.15em] flex-shrink-0"
          data-testid="crisis-disclaimer-dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
};
